import { useEffect, useState } from 'react'
import { apiClient } from '@/analysis/apiClient'
import type { ActiveFilters } from '@/types'
import type { Query } from '@/analysis/apiTypes'
import { toFilterQuery } from '@/analysis/queryBuilder'
import { toApiDimensionType } from '@/analysis/dimensionApiMap'
import type { SearchSliceNode } from '@/analysis/searchContract'
import type { Dimension } from '@/types'
import type { Selection, TimeWindow } from '@/views/deepDiveView/types'
import { stableStringify, toApiWindow, windowLengthMs } from '@/views/deepDiveView/TimeWindow'
import {
  deepDiveRowsFromNodes,
  mergeDeepDiveRows,
  type DeepDiveRow,
} from '@/views/deepDiveView/deepDiveRows'

/**
 * How many multiples of the visible window we keep for Stream details history.
 * Matches the chart buffer in useSeriesData.
 */
const BUFFER_MULTIPLIER = 3

type Args = {
  selected: Selection | null
  drillStream: string | null
  timeMode: 'live' | 'historic'
  timeWindow: TimeWindow
  historicTs: number | null | undefined
  historicFromTs?: number | null | undefined
  filters: ActiveFilters
  /** Live WS snapshot — merge ticks into the buffer; do not replace history. */
  searchNodes: ReadonlyArray<SearchSliceNode> | null | undefined
}

const buildTimeArg = (
  timeMode: 'live' | 'historic',
  timeWindow: TimeWindow,
  historicTs: number | null | undefined,
  historicFromTs?: number | null | undefined,
) => {
  if (timeMode === 'historic' && timeWindow === 'custom' && historicFromTs != null && historicTs != null) {
    return {
      mode: 'historic' as const,
      window: 'custom' as const,
      startTs: Math.min(historicFromTs, historicTs),
      endTs: Math.max(historicFromTs, historicTs),
    }
  }
  const window = toApiWindow(timeWindow)
  return timeMode === 'historic'
    ? { mode: 'historic' as const, window, startTs: historicTs ?? undefined, endTs: historicTs ?? undefined }
    : { mode: 'live' as const, window }
}

const buildAnalysisQuery = (
  selected: Selection,
  filters: ActiveFilters,
  time: ReturnType<typeof buildTimeArg>,
): Query => {
  const isStream = selected.dimension === 'stream'
  return {
    view: 'analysis',
    dimension: toApiDimensionType((isStream ? 'continent' : selected.dimension) as Dimension),
    slice: isStream
      ? { type: selected.sliceType ?? 'global', value: selected.sliceValue ?? '*' }
      : { type: selected.dimension, value: selected.value },
    time,
    filters: toFilterQuery(filters, { streamId: isStream ? selected.value : null }),
    limit: 5000,
    offset: 0,
  }
}

const bufferLengthMs = (timeWindow: TimeWindow): number => {
  const len = windowLengthMs(toApiWindow(timeWindow))
  return len === Infinity ? Infinity : len * BUFFER_MULTIPLIER
}

/**
 * Stream-details row buffer for Deep Dive.
 *
 *  - Seeds from `POST /api/analysis` when selection / time / filters change.
 *  - Live WS ticks upsert into the buffer (new window → push; same window →
 *    update active row). History is retained within the buffer window.
 */
export function useDeepDiveRows({
  selected,
  drillStream,
  timeMode,
  timeWindow,
  historicTs,
  historicFromTs,
  filters,
  searchNodes,
}: Args) {
  const [rows, setRows] = useState<DeepDiveRow[] | null>(null)
  const [error, setError] = useState<unknown>(null)
  const filtersKey = stableStringify(filters)

  useEffect(() => {
    if (!selected) {
      setRows(null)
      setError(null)
      return
    }
    let cancelled = false
    const time = buildTimeArg(timeMode, timeWindow, historicTs, historicFromTs)
    const bufferMs = bufferLengthMs(timeWindow)

    void (async () => {
      try {
        const query = buildAnalysisQuery(selected, filters, time)
        const res = await apiClient.analysisSearch(query)
        if (cancelled) return
        const seeded = deepDiveRowsFromNodes(res.nodes, drillStream)
        setRows(mergeDeepDiveRows([], seeded, bufferMs))
        setError(null)
      } catch (e) {
        if (cancelled) return
        setRows([])
        setError(e)
      }
    })()
    return () => { cancelled = true }
  }, [
    selected?.dimension,
    selected?.value,
    selected?.sliceType,
    selected?.sliceValue,
    drillStream,
    timeMode,
    timeWindow,
    historicTs,
    historicFromTs,
    filtersKey,
  ])

  useEffect(() => {
    if (!selected || !searchNodes) return
    if (timeMode !== 'live') return
    const incoming = deepDiveRowsFromNodes(searchNodes, drillStream)
    if (!incoming.length) return
    const bufferMs = bufferLengthMs(timeWindow)
    setRows((prev) => mergeDeepDiveRows(prev ?? [], incoming, bufferMs))
  }, [selected, drillStream, searchNodes, timeMode, timeWindow])

  return { rows: rows ?? [], error }
}
